import { useMemo } from "react";
import { useFirestore } from "@/hooks/firestore/core/useFirestore";
import { eventCount } from "@/hooks/firestore/models/eventCount";
import set from "lodash/set";
import { useHasPermission } from "@/features/auth/hooks/usePermission";

type Category =
  | "matches"
  | "liveMatches"
  | "prematchMatches"
  | "outrights"
  | "pickems"
  | "racing";

const useEventCounts = () => {
  const { data, isLoading, error } = useFirestore(
    "generatedEventCounts",
    eventCount.array(),
    {
      keyPath: "type",
    },
  );

  const canViewLiveBets = useHasPermission("viewLiveMarkets");

  const eventCounts = useMemo(() => {
    if (!data) {
      return;
    }

    return data.reduce(
      (
        result: Record<Category, Record<string, number>>,
        currentEventCounts,
      ) => {
        const [category, ...sportParts] = currentEventCounts.type.split(
          "_",
        ) as [Category, string[]];
        const sport = sportParts.join("_").toLowerCase();

        const prematchCount = currentEventCounts.prematch || 0;
        const liveCount = currentEventCounts.live || 0;
        const count = canViewLiveBets
          ? prematchCount + liveCount
          : prematchCount;

        set(result, `${category}.${sport}`, count);
        set(
          result,
          `${category}.total`,
          (result[category]?.total || 0) + count,
        );

        if (category === "matches") {
          set(result, `prematchMatches.${sport}`, prematchCount);
          set(
            result,
            `prematchMatches.total`,
            (result.prematchMatches?.total || 0) + prematchCount,
          );

          if (canViewLiveBets) {
            set(result, `liveMatches.${sport}`, liveCount);
            set(
              result,
              `liveMatches.total`,
              (result.liveMatches?.total || 0) + liveCount,
            );
          }
        }

        return result;
      },
      {
        matches: {
          total: 0,
        },
        liveMatches: {
          total: 0,
        },
        prematchMatches: {
          total: 0,
        },
        outrights: {
          total: 0,
        },
        pickems: {
          total: 0,
        },
        racing: {
          total: 0,
        },
      },
    );
  }, [data, canViewLiveBets]);

  return {
    eventCounts,
    isLoading,
    error,
  };
};

type EventCounts = Exclude<
  ReturnType<typeof useEventCounts>["eventCounts"],
  undefined
>;

export { useEventCounts, type EventCounts };
